import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, Megaphone, Mail, MessageCircle, Upload, Plus, Play, Pause, Ban, Trash2, RefreshCw,
  Users, Send, CheckCircle2, Eye, MousePointerClick, XCircle, ShieldOff, Clock, AlertTriangle, Info,
} from "lucide-react";
import { apiSuperAdmin, mensajeError } from "../../api/client";
import { Spinner } from "../../components/ui/Spinner";
import { RelojFechaHora } from "../../components/ui/RelojFechaHora";
import { Badge } from "../../components/ui/Badge";
import { useSuperAdminAuth } from "../../context/SuperAdminAuthContext";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
type TipoCampana = "EMAIL" | "WHATSAPP";
type EstadoCampana = "BORRADOR" | "PROGRAMADA" | "ENVIANDO" | "PAUSADA" | "COMPLETADA" | "CANCELADA";
type EstadoContacto = "PENDIENTE" | "ENVIANDO" | "ENVIADO" | "ENTREGADO" | "LEIDO" | "CLIC" | "FALLIDO" | "RECHAZADO" | "OMITIDO";

interface Campana {
  id: string;
  nombre: string;
  tipo: TipoCampana;
  estado: EstadoCampana;
  asunto?: string | null;
  cuerpoHtml?: string | null;
  cuerpoTexto?: string | null;
  whatsappPlantillaNombre?: string | null;
  whatsappPlantillaIdioma?: string | null;
  whatsappVariables?: string[] | null;
  columnaNombre?: string | null;
  columnaCorreo?: string | null;
  columnaTelefono?: string | null;
  csvEncabezados?: string[] | null;
  intervaloMs: number;
  programadaPara?: string | null;
  iniciadaEn?: string | null;
  completadaEn?: string | null;
  totalContactos: number;
  enviados: number;
  entregados: number;
  leidos: number;
  clics: number;
  fallidos: number;
  rechazados: number;
  createdAt: string;
}

interface ContactoCampana {
  id: string;
  nombre?: string | null;
  correo?: string | null;
  telefono?: string | null;
  estado: EstadoContacto;
  errorDetalle?: string | null;
  enviadoEn?: string | null;
  entregadoEn?: string | null;
  leidoEn?: string | null;
  clicEn?: string | null;
}

interface Bloqueado {
  id: string;
  tipo: TipoCampana;
  valor: string;
  motivo?: string | null;
  createdAt: string;
}

interface ResumenGlobal {
  totalCampanas: number;
  totalContactos: number;
  enviados: number;
  entregados: number;
  leidos: number;
  clics: number;
  fallidos: number;
  rechazados: number;
  campanasActivas: number;
  porEstado: Record<string, number>;
}

const ETIQUETA_ESTADO: Record<EstadoCampana, string> = {
  BORRADOR: "Borrador",
  PROGRAMADA: "Programada",
  ENVIANDO: "Enviando",
  PAUSADA: "Pausada",
  COMPLETADA: "Completada",
  CANCELADA: "Cancelada",
};
const COLOR_ESTADO: Record<EstadoCampana, "gris" | "azul" | "verde" | "amarillo" | "rojo" | "morado"> = {
  BORRADOR: "gris",
  PROGRAMADA: "azul",
  ENVIANDO: "morado",
  PAUSADA: "amarillo",
  COMPLETADA: "verde",
  CANCELADA: "rojo",
};

const ETIQUETA_ESTADO_CONTACTO: Record<EstadoContacto, string> = {
  PENDIENTE: "Pendiente",
  ENVIANDO: "Enviando",
  ENVIADO: "Enviado",
  ENTREGADO: "Entregado",
  LEIDO: "Leído / abierto",
  CLIC: "Hizo clic",
  FALLIDO: "Falló",
  RECHAZADO: "Rechazado",
  OMITIDO: "Omitido",
};
const COLOR_ESTADO_CONTACTO: Record<EstadoContacto, "gris" | "azul" | "verde" | "amarillo" | "rojo" | "morado"> = {
  PENDIENTE: "gris",
  ENVIANDO: "azul",
  ENVIADO: "azul",
  ENTREGADO: "morado",
  LEIDO: "verde",
  CLIC: "verde",
  FALLIDO: "rojo",
  RECHAZADO: "rojo",
  OMITIDO: "amarillo",
};

const PRESETS_INTERVALO: Record<TipoCampana, { valor: number; etiqueta: string }[]> = {
  EMAIL: [
    { valor: 2000, etiqueta: "2 seg — listas chicas (menos de 500)" },
    { valor: 3000, etiqueta: "3 seg — recomendado (500 a 3,000)" },
    { valor: 6000, etiqueta: "6 seg — listas grandes o cuenta nueva" },
  ],
  WHATSAPP: [
    { valor: 4000, etiqueta: "4 seg — listas chicas (menos de 250)" },
    { valor: 6000, etiqueta: "6 seg — recomendado" },
    { valor: 10000, etiqueta: "10 seg — cuenta nueva o números sensibles" },
  ],
};

function Tarjeta({ icono: Icono, titulo, valor, sub }: { icono: any; titulo: string; valor: string | number; sub?: string }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 text-slate-400">
        <Icono size={15} />
        <span className="text-xs font-medium">{titulo}</span>
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-800">{valor}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

function pct(parte: number, total: number): string {
  if (!total) return "0%";
  return `${Math.round((parte / total) * 100)}%`;
}

// ---------------------------------------------------------------------------
export default function Campanas() {
  const { superAdmin, cargando: cargandoAuth } = useSuperAdminAuth();
  const [vista, setVista] = useState<"lista" | "nueva" | "detalle" | "bloqueados">("lista");
  const [campanas, setCampanas] = useState<Campana[]>([]);
  const [resumen, setResumen] = useState<ResumenGlobal | null>(null);
  const [cargando, setCargando] = useState(true);
  const [campanaActivaId, setCampanaActivaId] = useState<string | null>(null);
  const [accionando, setAccionando] = useState<string | null>(null);

  async function cargar() {
    setCargando(true);
    try {
      const [rCampanas, rResumen] = await Promise.all([
        apiSuperAdmin.get("/superadmin/campanas"),
        apiSuperAdmin.get("/superadmin/campanas/resumen-global"),
      ]);
      setCampanas(rCampanas.data.items);
      setResumen(rResumen.data);
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  // Mientras haya alguna campaña activa, refrescamos la lista solas cada
  // 8 segundos para que los contadores se vean avanzar sin que el super
  // admin tenga que recargar la página a mano.
  useEffect(() => {
    const hayActivas = campanas.some((c) => c.estado === "ENVIANDO" || c.estado === "PROGRAMADA");
    if (!hayActivas || vista !== "lista") return;
    const id = setInterval(cargar, 8000);
    return () => clearInterval(id);
  }, [campanas, vista]);

  async function iniciar(c: Campana) {
    setAccionando(c.id);
    try {
      const res = await apiSuperAdmin.post(`/superadmin/campanas/${c.id}/iniciar`);
      if (res.data.aviso) alert(res.data.aviso);
      await cargar();
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setAccionando(null);
    }
  }

  async function pausar(c: Campana) {
    setAccionando(c.id);
    try {
      await apiSuperAdmin.post(`/superadmin/campanas/${c.id}/pausar`);
      await cargar();
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setAccionando(null);
    }
  }

  async function cancelar(c: Campana) {
    if (!confirm(`¿Cancelar la campaña "${c.nombre}"? Los contactos que todavía no recibieron el mensaje ya no lo van a recibir.`)) return;
    setAccionando(c.id);
    try {
      await apiSuperAdmin.post(`/superadmin/campanas/${c.id}/cancelar`);
      await cargar();
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setAccionando(null);
    }
  }

  async function eliminar(c: Campana) {
    if (!confirm(`¿Eliminar el borrador "${c.nombre}"? No se puede deshacer.`)) return;
    setAccionando(c.id);
    try {
      await apiSuperAdmin.delete(`/superadmin/campanas/${c.id}`);
      await cargar();
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setAccionando(null);
    }
  }

  if (!cargandoAuth && !superAdmin) return <Navigate to="/superadmin/login" replace />;

  if (vista === "nueva") {
    return (
      <Envoltura>
        <NuevaCampana
          onCancelar={() => setVista("lista")}
          onCreada={() => {
            setVista("lista");
            cargar();
          }}
        />
      </Envoltura>
    );
  }

  if (vista === "detalle" && campanaActivaId) {
    return (
      <Envoltura>
        <DetalleCampana
          campanaId={campanaActivaId}
          onVolver={() => {
            setVista("lista");
            cargar();
          }}
        />
      </Envoltura>
    );
  }

  if (vista === "bloqueados") {
    return (
      <Envoltura>
        <Bloqueados onVolver={() => setVista("lista")} />
      </Envoltura>
    );
  }

  return (
    <Envoltura>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <Megaphone size={22} className="text-blue-500" /> Campañas masivas
          </h1>
          <p className="mt-1 text-sm text-slate-500">Correo electrónico y WhatsApp, envío secuencial a partir de un CSV.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => setVista("bloqueados")}>
            <ShieldOff size={16} /> Lista de bloqueados
          </button>
          <button className="btn-primary" onClick={() => setVista("nueva")}>
            <Plus size={16} /> Nueva campaña
          </button>
        </div>
      </div>

      {cargando || !resumen ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Tarjeta icono={Megaphone} titulo="Campañas totales" valor={resumen.totalCampanas} sub={`${resumen.campanasActivas} activas ahora`} />
            <Tarjeta icono={Send} titulo="Mensajes enviados" valor={resumen.enviados} sub={`de ${resumen.totalContactos} contactos cargados`} />
            <Tarjeta icono={Eye} titulo="Tasa de apertura" valor={pct(resumen.leidos, resumen.enviados)} sub={`${resumen.leidos} abiertos/leídos`} />
            <Tarjeta icono={MousePointerClick} titulo="Tasa de clics" valor={pct(resumen.clics, resumen.enviados)} sub={`${resumen.clics} clics`} />
          </div>

          {campanas.length === 0 ? (
            <div className="card flex flex-col items-center gap-2 p-12 text-center text-slate-400">
              <Megaphone size={32} />
              <p className="text-sm">Todavía no has creado ninguna campaña.</p>
              <button className="btn-primary mt-2" onClick={() => setVista("nueva")}>
                <Plus size={16} /> Crear la primera
              </button>
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase text-slate-400">
                    <th className="px-4 py-3">Campaña</th>
                    <th className="px-4 py-3">Canal</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Progreso</th>
                    <th className="px-4 py-3">Entregado / Leído / Clic</th>
                    <th className="px-4 py-3">Fallidos</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {campanas.map((c) => (
                    <tr key={c.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                      <td className="px-4 py-3">
                        <button className="font-medium text-slate-700 hover:text-blue-600" onClick={() => { setCampanaActivaId(c.id); setVista("detalle"); }}>
                          {c.nombre}
                        </button>
                        <div className="text-xs text-slate-400">{new Date(c.createdAt).toLocaleDateString("es-MX")}</div>
                      </td>
                      <td className="px-4 py-3">
                        {c.tipo === "EMAIL" ? (
                          <span className="inline-flex items-center gap-1 text-slate-600"><Mail size={14} /> Correo</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-600"><MessageCircle size={14} /> WhatsApp</span>
                        )}
                      </td>
                      <td className="px-4 py-3"><Badge color={COLOR_ESTADO[c.estado]}>{ETIQUETA_ESTADO[c.estado]}</Badge></td>
                      <td className="px-4 py-3">
                        <div className="w-28">
                          <div className="h-1.5 rounded-full bg-slate-100">
                            <div className="h-1.5 rounded-full bg-blue-500" style={{ width: pct(c.enviados, c.totalContactos) }} />
                          </div>
                          <div className="mt-1 text-xs text-slate-400">{c.enviados} / {c.totalContactos}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {c.entregados} / {c.leidos} / {c.clics}
                      </td>
                      <td className="px-4 py-3">
                        {c.fallidos > 0 ? <span className="text-red-500">{c.fallidos}</span> : <span className="text-slate-300">0</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1.5">
                          {(c.estado === "BORRADOR" || c.estado === "PAUSADA") && (
                            <button
                              title="Iniciar"
                              disabled={accionando === c.id}
                              onClick={() => iniciar(c)}
                              className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
                            >
                              <Play size={16} />
                            </button>
                          )}
                          {(c.estado === "ENVIANDO" || c.estado === "PROGRAMADA") && (
                            <button
                              title="Pausar"
                              disabled={accionando === c.id}
                              onClick={() => pausar(c)}
                              className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-50 disabled:opacity-40"
                            >
                              <Pause size={16} />
                            </button>
                          )}
                          {!["COMPLETADA", "CANCELADA"].includes(c.estado) && (
                            <button
                              title="Cancelar"
                              disabled={accionando === c.id}
                              onClick={() => cancelar(c)}
                              className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-40"
                            >
                              <Ban size={16} />
                            </button>
                          )}
                          {c.estado === "BORRADOR" && (
                            <button
                              title="Eliminar"
                              disabled={accionando === c.id}
                              onClick={() => eliminar(c)}
                              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-40"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Envoltura>
  );
}

function Envoltura({ children }: { children: React.ReactNode }) {
  return (
    <div className="fondo-marca-agua min-h-screen">
      <header className="flex items-center justify-between bg-gradient-to-r from-slate-900 via-slate-900 to-blue-950 px-6 py-4 text-white">
        <Link to="/superadmin" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
          <ArrowLeft size={15} /> Volver al panel
        </Link>
        <RelojFechaHora claro />
      </header>
      <main className="mx-auto max-w-6xl p-6">{children}</main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formulario de nueva campaña
// ---------------------------------------------------------------------------
function NuevaCampana({ onCancelar, onCreada }: { onCancelar: () => void; onCreada: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState<TipoCampana>("EMAIL");

  const [archivoNombre, setArchivoNombre] = useState("");
  const [csvTexto, setCsvTexto] = useState("");
  const [encabezados, setEncabezados] = useState<string[]>([]);
  const [muestra, setMuestra] = useState<Record<string, string>[]>([]);
  const [totalFilas, setTotalFilas] = useState(0);
  const [analizando, setAnalizando] = useState(false);
  const [validez, setValidez] = useState<{ correosValidos?: number | null; correosInvalidos?: number | null; telefonosValidos?: number | null; telefonosInvalidos?: number | null }>({});

  const [columnaNombre, setColumnaNombre] = useState("");
  const [columnaCorreo, setColumnaCorreo] = useState("");
  const [columnaTelefono, setColumnaTelefono] = useState("");

  const [asunto, setAsunto] = useState("");
  const [cuerpoHtml, setCuerpoHtml] = useState("");
  const [cuerpoTexto, setCuerpoTexto] = useState("");

  const [whatsappPlantillaNombre, setWhatsappPlantillaNombre] = useState("");
  const [whatsappPlantillaIdioma, setWhatsappPlantillaIdioma] = useState("es_MX");
  const [whatsappVariables, setWhatsappVariables] = useState<string[]>([]);

  const [intervaloMs, setIntervaloMs] = useState(PRESETS_INTERVALO.EMAIL[1].valor);
  const [programar, setProgramar] = useState(false);
  const [programadaPara, setProgramadaPara] = useState("");

  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setIntervaloMs(PRESETS_INTERVALO[tipo][1].valor);
  }, [tipo]);

  async function analizarCsv(columnaCorreoVal?: string, columnaTelefonoVal?: string) {
    if (!csvTexto) return;
    setAnalizando(true);
    setError("");
    try {
      const res = await apiSuperAdmin.post("/superadmin/campanas/csv/vista-previa", {
        csv: csvTexto,
        columnaCorreo: columnaCorreoVal || undefined,
        columnaTelefono: columnaTelefonoVal || undefined,
      });
      setEncabezados(res.data.encabezados);
      setMuestra(res.data.muestra);
      setTotalFilas(res.data.totalFilas);
      setValidez({
        correosValidos: res.data.correosValidos,
        correosInvalidos: res.data.correosInvalidos,
        telefonosValidos: res.data.telefonosValidos,
        telefonosInvalidos: res.data.telefonosInvalidos,
      });
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setAnalizando(false);
    }
  }

  // Excel en Windows (sobre todo en español/Latinoamérica) guarda "CSV
  // (delimitado por comas)" en Windows-1252 (ANSI), no en UTF-8. Si el
  // archivo se lee siempre forzando UTF-8, los acentos/eñes quedan mal
  // (aparecen símbolos raros o el carácter de reemplazo "�"). Para evitar
  // eso, leemos los bytes crudos y probamos primero UTF-8 en modo estricto;
  // si el archivo NO es UTF-8 válido, se vuelve a leer como Windows-1252 —
  // así funciona igual con un CSV exportado de Excel o de Google Sheets.
  function decodificarCsv(buffer: ArrayBuffer): string {
    try {
      return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
    } catch {
      return new TextDecoder("windows-1252").decode(buffer);
    }
  }

  function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    setArchivoNombre(archivo.name);
    const lector = new FileReader();
    lector.onload = () => {
      const buffer = lector.result as ArrayBuffer;
      const texto = decodificarCsv(buffer);
      setCsvTexto(texto);
      setColumnaNombre("");
      setColumnaCorreo("");
      setColumnaTelefono("");
    };
    lector.readAsArrayBuffer(archivo);
  }

  // En cuanto tenemos el texto del CSV, lo analizamos una primera vez (sin
  // mapeo) solo para sacar los encabezados y poder llenar los selects.
  useEffect(() => {
    if (csvTexto) analizarCsv();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvTexto]);

  function agregarVariable() {
    setWhatsappVariables((v) => [...v, encabezados[0] ?? ""]);
  }
  function quitarVariable(i: number) {
    setWhatsappVariables((v) => v.filter((_, idx) => idx !== i));
  }
  function cambiarVariable(i: number, valor: string) {
    setWhatsappVariables((v) => v.map((x, idx) => (idx === i ? valor : x)));
  }

  function insertarToken(token: string, setter: (fn: (t: string) => string) => void) {
    setter((actual) => `${actual}{{${token}}}`);
  }

  async function crear() {
    setError("");
    if (!nombre.trim()) return setError("Ponle un nombre a la campaña.");
    if (!csvTexto) return setError("Carga un archivo CSV con tus contactos.");
    if (tipo === "EMAIL" && !columnaCorreo) return setError("Indica cuál columna del archivo tiene el correo.");
    if (tipo === "WHATSAPP" && !columnaTelefono) return setError("Indica cuál columna del archivo tiene el teléfono.");
    if (tipo === "EMAIL" && (!asunto.trim() || !cuerpoHtml.trim())) return setError("Falta el asunto o el contenido del correo.");
    if (tipo === "WHATSAPP" && !whatsappPlantillaNombre.trim()) return setError("Falta el nombre de la plantilla de WhatsApp.");
    if (programar && !programadaPara) return setError("Elige la fecha y hora para programar el envío.");

    setGuardando(true);
    try {
      const res = await apiSuperAdmin.post("/superadmin/campanas", {
        nombre,
        tipo,
        csv: csvTexto,
        columnaNombre: columnaNombre || undefined,
        columnaCorreo: tipo === "EMAIL" ? columnaCorreo : undefined,
        columnaTelefono: tipo === "WHATSAPP" ? columnaTelefono : undefined,
        asunto: tipo === "EMAIL" ? asunto : undefined,
        cuerpoHtml: tipo === "EMAIL" ? cuerpoHtml : undefined,
        cuerpoTexto: tipo === "EMAIL" ? cuerpoTexto || undefined : undefined,
        whatsappPlantillaNombre: tipo === "WHATSAPP" ? whatsappPlantillaNombre : undefined,
        whatsappPlantillaIdioma: tipo === "WHATSAPP" ? whatsappPlantillaIdioma : undefined,
        whatsappVariables: tipo === "WHATSAPP" ? whatsappVariables : undefined,
        intervaloMs,
      });

      const id = res.data.campana.id;
      const r = res.data.resumenCarga;
      const avisoOmitidos = r.omitidosInvalido + r.omitidosDuplicado + r.omitidosBloqueado > 0
        ? `\n\nSe omitieron ${r.omitidosInvalido} por dato inválido, ${r.omitidosDuplicado} duplicados y ${r.omitidosBloqueado} por estar en tu lista de bloqueados.`
        : "";
      const seguirIniciando = confirm(
        `Campaña creada con ${r.listosParaEnviar} contactos listos para enviar.${avisoOmitidos}\n\n¿Quieres iniciarla ahora${programar ? " (quedará programada para la fecha elegida)" : ""}?`
      );
      if (seguirIniciando) {
        await apiSuperAdmin.post(`/superadmin/campanas/${id}/iniciar`, programar ? { programadaPara: new Date(programadaPara).toISOString() } : {});
      }
      onCreada();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div>
      <button className="btn-ghost mb-4" onClick={onCancelar}>
        <ArrowLeft size={16} /> Volver a la lista
      </button>
      <h1 className="mb-6 text-xl font-bold text-slate-800">Nueva campaña</h1>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-600">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      <div className="space-y-6">
        {/* 1. Datos básicos */}
        <div className="card p-5">
          <h2 className="mb-4 text-sm font-semibold text-slate-700">1. Datos básicos</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Nombre de la campaña</label>
              <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej. Lanzamiento julio — clínicas CDMX" />
            </div>
            <div>
              <label className="label">Canal</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setTipo("EMAIL")}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium ${tipo === "EMAIL" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}
                >
                  <Mail size={15} /> Correo
                </button>
                <button
                  type="button"
                  onClick={() => setTipo("WHATSAPP")}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium ${tipo === "WHATSAPP" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}
                >
                  <MessageCircle size={15} /> WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 2. CSV */}
        <div className="card p-5">
          <h2 className="mb-4 text-sm font-semibold text-slate-700">2. Lista de contactos (CSV)</h2>
          <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={onArchivo} />
          <button type="button" className="btn-secondary" onClick={() => inputRef.current?.click()}>
            <Upload size={16} /> {archivoNombre || "Elegir archivo CSV"}
          </button>
          <p className="mt-2 text-xs text-slate-400">
            Cualquier CSV sirve, con los encabezados que ya tenga tu archivo (nombre, correo, teléfono, ciudad, lo que sea). En el
            siguiente paso eliges qué columna es cada cosa.
          </p>

          {analizando && <div className="mt-3"><Spinner className="h-4 w-4" /></div>}

          {encabezados.length > 0 && (
            <div className="mt-4 space-y-4">
              <p className="text-xs text-slate-500">{totalFilas} contactos encontrados en el archivo.</p>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="label">Columna de nombre (opcional)</label>
                  <select className="input" value={columnaNombre} onChange={(e) => setColumnaNombre(e.target.value)}>
                    <option value="">— No usar —</option>
                    {encabezados.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                {tipo === "EMAIL" && (
                  <div>
                    <label className="label">Columna de correo *</label>
                    <select
                      className="input"
                      value={columnaCorreo}
                      onChange={(e) => { setColumnaCorreo(e.target.value); analizarCsv(e.target.value, undefined); }}
                    >
                      <option value="">— Elegir —</option>
                      {encabezados.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {validez.correosValidos !== undefined && validez.correosValidos !== null && (
                      <p className="mt-1 text-xs text-slate-400">
                        {validez.correosValidos} válidos, {validez.correosInvalidos} inválidos o vacíos (se van a omitir).
                      </p>
                    )}
                  </div>
                )}
                {tipo === "WHATSAPP" && (
                  <div>
                    <label className="label">Columna de teléfono *</label>
                    <select
                      className="input"
                      value={columnaTelefono}
                      onChange={(e) => { setColumnaTelefono(e.target.value); analizarCsv(undefined, e.target.value); }}
                    >
                      <option value="">— Elegir —</option>
                      {encabezados.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {validez.telefonosValidos !== undefined && validez.telefonosValidos !== null && (
                      <p className="mt-1 text-xs text-slate-400">
                        {validez.telefonosValidos} válidos, {validez.telefonosInvalidos} inválidos o vacíos (se van a omitir).
                      </p>
                    )}
                  </div>
                )}
              </div>

              {muestra.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-slate-100">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-left text-slate-400">
                        {encabezados.map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 font-medium">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {muestra.slice(0, 4).map((fila, i) => (
                        <tr key={i} className="border-t border-slate-50">
                          {encabezados.map((h) => <td key={h} className="whitespace-nowrap px-3 py-2 text-slate-500">{fila[h]}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3. Contenido */}
        <div className="card p-5">
          <h2 className="mb-1 text-sm font-semibold text-slate-700">3. Contenido del mensaje</h2>
          {encabezados.length > 0 && (
            <p className="mb-4 text-xs text-slate-400">
              Personaliza con las columnas de tu archivo: {encabezados.map((h) => (
                <code key={h} className="mx-0.5 rounded bg-slate-100 px-1 py-0.5">{`{{${h}}}`}</code>
              ))}
            </p>
          )}

          {tipo === "EMAIL" ? (
            <div className="space-y-4">
              <div>
                <label className="label">Asunto</label>
                <input className="input" value={asunto} onChange={(e) => setAsunto(e.target.value)} placeholder="Ej. {{Nombre}}, así se ve tu veterinaria organizada" />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="label mb-0">Cuerpo del correo (HTML)</label>
                  {encabezados.length > 0 && (
                    <select className="rounded-lg border border-slate-200 px-2 py-1 text-xs" onChange={(e) => { if (e.target.value) insertarToken(e.target.value, setCuerpoHtml); e.target.value = ""; }}>
                      <option value="">+ insertar variable</option>
                      {encabezados.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  )}
                </div>
                <textarea className="input h-40 font-mono text-xs" value={cuerpoHtml} onChange={(e) => setCuerpoHtml(e.target.value)} placeholder="<p>Hola {{Nombre}}, ...</p>" />
              </div>
              <div>
                <label className="label">Versión en texto plano (opcional, recomendado)</label>
                <textarea className="input h-20" value={cuerpoTexto} onChange={(e) => setCuerpoTexto(e.target.value)} placeholder="Hola {{Nombre}}, ..." />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-2 rounded-xl bg-blue-50 p-3 text-xs text-blue-700">
                <Info size={15} className="mt-0.5 shrink-0" />
                Meta exige que cualquier WhatsApp que inicia el negocio use una <strong>plantilla ya aprobada</strong>. Créala una sola
                vez en Meta Business Manager (WhatsApp Manager → Plantillas de mensaje) y aquí solo indicas su nombre y qué columna
                del CSV va en cada variable.
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">Nombre exacto de la plantilla en Meta</label>
                  <input className="input" value={whatsappPlantillaNombre} onChange={(e) => setWhatsappPlantillaNombre(e.target.value)} placeholder="ej. promo_julio_negocios" />
                </div>
                <div>
                  <label className="label">Idioma de la plantilla</label>
                  <input className="input" value={whatsappPlantillaIdioma} onChange={(e) => setWhatsappPlantillaIdioma(e.target.value)} />
                </div>
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="label mb-0">Variables de la plantilla (en orden: {"{{1}}"}, {"{{2}}"}...)</label>
                  <button type="button" className="btn-ghost text-xs" onClick={agregarVariable} disabled={encabezados.length === 0}>
                    <Plus size={14} /> Agregar variable
                  </button>
                </div>
                {whatsappVariables.length === 0 && <p className="text-xs text-slate-400">Esta plantilla no tiene variables, o no las has agregado.</p>}
                <div className="space-y-2">
                  {whatsappVariables.map((v, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-8 shrink-0 text-xs text-slate-400">{`{{${i + 1}}}`}</span>
                      <select className="input" value={v} onChange={(e) => cambiarVariable(i, e.target.value)}>
                        {encabezados.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                      <button type="button" onClick={() => quitarVariable(i)} className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 4. Envío */}
        <div className="card p-5">
          <h2 className="mb-4 text-sm font-semibold text-slate-700">4. Ritmo de envío</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Pausa entre cada mensaje</label>
              <select className="input" value={intervaloMs} onChange={(e) => setIntervaloMs(Number(e.target.value))}>
                {PRESETS_INTERVALO[tipo].map((p) => <option key={p.valor} value={p.valor}>{p.etiqueta}</option>)}
              </select>
              <p className="mt-1 text-xs text-slate-400">
                El envío siempre es uno por uno, nunca todos de golpe — esto es lo que protege tu cuenta de un bloqueo.
              </p>
            </div>
            <div>
              <label className="label">¿Cuándo se manda?</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setProgramar(false)} className={`flex-1 rounded-xl border px-3 py-2 text-sm ${!programar ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}>Ahora</button>
                <button type="button" onClick={() => setProgramar(true)} className={`flex-1 rounded-xl border px-3 py-2 text-sm ${programar ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}>Programar</button>
              </div>
              {programar && (
                <input type="datetime-local" className="input mt-2" value={programadaPara} onChange={(e) => setProgramadaPara(e.target.value)} />
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onCancelar}>Cancelar</button>
          <button className="btn-primary" onClick={crear} disabled={guardando}>
            {guardando ? <Spinner className="h-4 w-4" /> : <CheckCircle2 size={16} />} Crear campaña
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detalle de campaña
// ---------------------------------------------------------------------------
function DetalleCampana({ campanaId, onVolver }: { campanaId: string; onVolver: () => void }) {
  const [campana, setCampana] = useState<Campana | null>(null);
  const [contactos, setContactos] = useState<ContactoCampana[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [cargando, setCargando] = useState(true);

  async function cargar() {
    try {
      const [rCampana, rContactos] = await Promise.all([
        apiSuperAdmin.get(`/superadmin/campanas/${campanaId}`),
        apiSuperAdmin.get(`/superadmin/campanas/${campanaId}/contactos`, { params: { pagina, porPagina: 50, estado: filtroEstado || undefined } }),
      ]);
      setCampana(rCampana.data);
      setContactos(rContactos.data.items);
      setTotal(rContactos.data.total);
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campanaId, pagina, filtroEstado]);

  useEffect(() => {
    if (!campana || !["ENVIANDO", "PROGRAMADA"].includes(campana.estado)) return;
    const id = setInterval(cargar, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campana?.estado]);

  if (cargando || !campana) {
    return <div className="flex justify-center py-16"><Spinner /></div>;
  }

  const totalPaginas = Math.max(1, Math.ceil(total / 50));

  return (
    <div>
      <button className="btn-ghost mb-4" onClick={onVolver}><ArrowLeft size={16} /> Volver a la lista</button>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">{campana.nombre}</h1>
          <div className="mt-1 flex items-center gap-2">
            <Badge color={COLOR_ESTADO[campana.estado]}>{ETIQUETA_ESTADO[campana.estado]}</Badge>
            <span className="text-xs text-slate-400">{campana.tipo === "EMAIL" ? "Correo electrónico" : "WhatsApp"}</span>
          </div>
        </div>
        <button className="btn-secondary" onClick={cargar}><RefreshCw size={15} /> Actualizar</button>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Tarjeta icono={Users} titulo="Contactos" valor={campana.totalContactos} />
        <Tarjeta icono={Send} titulo="Enviados" valor={campana.enviados} sub={pct(campana.enviados, campana.totalContactos)} />
        <Tarjeta icono={CheckCircle2} titulo="Entregados" valor={campana.entregados} />
        <Tarjeta icono={Eye} titulo="Leídos / abiertos" valor={campana.leidos} sub={pct(campana.leidos, campana.enviados)} />
        <Tarjeta icono={MousePointerClick} titulo="Clics" valor={campana.clics} sub={pct(campana.clics, campana.enviados)} />
        <Tarjeta icono={XCircle} titulo="Fallidos" valor={campana.fallidos} />
        <Tarjeta icono={ShieldOff} titulo="Rechazados / bloqueados" valor={campana.rechazados} />
        <Tarjeta icono={Clock} titulo="Pausa entre envíos" valor={`${Math.round(campana.intervaloMs / 1000)} seg`} />
      </div>

      <div className="card overflow-x-auto">
        <div className="flex items-center justify-between border-b border-slate-100 p-4">
          <h2 className="text-sm font-semibold text-slate-700">Contactos ({total})</h2>
          <select className="input w-auto text-xs" value={filtroEstado} onChange={(e) => { setFiltroEstado(e.target.value); setPagina(1); }}>
            <option value="">Todos los estados</option>
            {Object.entries(ETIQUETA_ESTADO_CONTACTO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase text-slate-400">
              <th className="px-4 py-3">Contacto</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {contactos.map((c) => (
              <tr key={c.id} className="border-b border-slate-50 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-700">{c.nombre || "—"}</div>
                  <div className="text-xs text-slate-400">{c.correo || c.telefono}</div>
                </td>
                <td className="px-4 py-3"><Badge color={COLOR_ESTADO_CONTACTO[c.estado]}>{ETIQUETA_ESTADO_CONTACTO[c.estado]}</Badge></td>
                <td className="px-4 py-3 text-xs text-slate-400">{c.errorDetalle || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPaginas > 1 && (
          <div className="flex items-center justify-center gap-3 border-t border-slate-100 p-3 text-sm">
            <button className="btn-ghost" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</button>
            <span className="text-xs text-slate-400">Página {pagina} de {totalPaginas}</span>
            <button className="btn-ghost" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>Siguiente</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lista de bloqueados (opt-out)
// ---------------------------------------------------------------------------
function Bloqueados({ onVolver }: { onVolver: () => void }) {
  const [items, setItems] = useState<Bloqueado[]>([]);
  const [cargando, setCargando] = useState(true);
  const [tipo, setTipo] = useState<TipoCampana>("EMAIL");
  const [valor, setValor] = useState("");
  const [motivo, setMotivo] = useState("");
  const [guardando, setGuardando] = useState(false);

  async function cargar() {
    setCargando(true);
    try {
      const res = await apiSuperAdmin.get("/superadmin/campanas/bloqueados");
      setItems(res.data.items);
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  async function agregar() {
    if (!valor.trim()) return;
    setGuardando(true);
    try {
      await apiSuperAdmin.post("/superadmin/campanas/bloqueados", { tipo, valor, motivo: motivo || undefined });
      setValor("");
      setMotivo("");
      await cargar();
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  async function quitar(id: string) {
    if (!confirm("¿Quitar de la lista de bloqueados? Podría volver a recibir campañas futuras.")) return;
    await apiSuperAdmin.delete(`/superadmin/campanas/bloqueados/${id}`);
    await cargar();
  }

  return (
    <div>
      <button className="btn-ghost mb-4" onClick={onVolver}><ArrowLeft size={16} /> Volver a la lista</button>
      <h1 className="mb-1 flex items-center gap-2 text-xl font-bold text-slate-800"><ShieldOff size={20} /> Lista de bloqueados</h1>
      <p className="mb-6 text-sm text-slate-500">
        Correos y teléfonos que se omiten automáticamente en cualquier campaña futura. Se agregan aquí manualmente (ej. alguien te
        pidió por otro medio que ya no le escribas) o automáticamente cuando un correo rebota o un mensaje falla repetidamente.
      </p>

      <div className="card mb-6 p-5">
        <h2 className="mb-4 text-sm font-semibold text-slate-700">Agregar manualmente</h2>
        <div className="grid gap-3 sm:grid-cols-4">
          <select className="input" value={tipo} onChange={(e) => setTipo(e.target.value as TipoCampana)}>
            <option value="EMAIL">Correo</option>
            <option value="WHATSAPP">WhatsApp</option>
          </select>
          <input className="input sm:col-span-2" placeholder={tipo === "EMAIL" ? "correo@ejemplo.com" : "5512345678"} value={valor} onChange={(e) => setValor(e.target.value)} />
          <input className="input" placeholder="Motivo (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
        <button className="btn-primary mt-3" onClick={agregar} disabled={guardando}>
          <Plus size={15} /> Agregar
        </button>
      </div>

      {cargando ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : items.length === 0 ? (
        <p className="text-center text-sm text-slate-400">No hay nadie en la lista de bloqueados todavía.</p>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase text-slate-400">
                <th className="px-4 py-3">Contacto</th>
                <th className="px-4 py-3">Canal</th>
                <th className="px-4 py-3">Motivo</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-b border-slate-50 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-700">{b.valor}</td>
                  <td className="px-4 py-3 text-slate-500">{b.tipo === "EMAIL" ? "Correo" : "WhatsApp"}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{b.motivo || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => quitar(b.id)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
